import { useEffect, useState, useCallback } from "react";
import {
  CurrencyDollar,
  Package,
  Users,
  Receipt,
  Plus,
  PencilSimple,
  Trash,
  UploadSimple,
  CircleNotch,
  X,
  Ticket,
  Truck,
  FloppyDisk,
  ChartLineUp,
  PaperPlaneTilt,
  CalendarCheck,
  ChatCircleText,
  EnvelopeSimple,
  DownloadSimple,
  Table,
  FileCsv,
  ArrowsClockwise,
  ArrowLineUp,
  ArrowLineDown,
  CheckCircle,
  WarningCircle,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import api, { API, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  name: "", category: "", short_description: "", description: "", price: "",
  cost: "", purity: "", sequence: "", molecular_formula: "", cas_number: "", batch_number: "", stock: "",
  featured: false, image_url: "", coa_file_path: "", coa_filename: "",
};

const CATEGORIES = ["Healing & Recovery", "Metabolic", "Cosmetic", "Longevity", "Nootropic"];
const ORDER_STATUSES = ["awaiting_payment", "paid", "processing", "shipped", "delivered", "cancelled"];

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-cyan/15 bg-navy-surface p-6">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-teal">{label}</span>
        <Icon size={20} weight="duotone" className="text-cyan" />
      </div>
      <p className="mt-3 font-mono text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function ProductFormDialog({ open, onClose, product, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ image: false, coa: false });

  useEffect(() => {
    if (product) setForm({ ...EMPTY, ...product, price: product.price ?? "", stock: product.stock ?? "" });
    else setForm(EMPTY);
  }, [product, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (file, kind) => {
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "image") set("image_url", `${API}/files/${data.path}`);
      else {
        set("coa_file_path", data.path);
        set("coa_filename", data.filename);
      }
      toast.success(`${kind === "image" ? "Image" : "COA"} uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.price) {
      toast.error("Name, category and price are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      cost: parseFloat(form.cost) || 0,
      stock: parseInt(form.stock) || 0,
    };
    try {
      if (product?.id) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success(product?.id ? "Product updated" : "Product created");
      onSaved();
      onClose();
    } catch {
      toast.error("Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full border border-cyan/15 bg-navy px-3 py-2.5 font-mono text-sm text-white placeholder:text-teal/40 outline-none focus:border-cyan/50";
  const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-teal";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-cyan/20 bg-navy-surface text-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight text-white">
            {product?.id ? "Edit Compound" : "New Compound"}
          </DialogTitle>
          <DialogDescription className="font-mono text-[11px] uppercase tracking-widest text-teal">
            Manage compound details, pricing, image and Certificate of Analysis.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="product-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Name *</label>
              <input data-testid="form-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Category *</label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="form-category" className="border-cyan/15 bg-navy font-mono text-sm text-white">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="border-cyan/20 bg-navy-surface font-mono text-sm text-white">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Short Description</label>
            <input data-testid="form-short-desc" className={inputCls} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Full Description</label>
            <textarea data-testid="form-desc" rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <div>
              <label className={labelCls}>Price (USD) *</label>
              <input data-testid="form-price" type="number" step="0.01" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Cost (USD)</label>
              <input data-testid="form-cost" type="number" step="0.01" className={inputCls} value={form.cost} onChange={(e) => set("cost", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Stock</label>
              <input data-testid="form-stock" type="number" className={inputCls} value={form.stock} onChange={(e) => set("stock", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Purity</label>
              <input data-testid="form-purity" className={inputCls} placeholder=">99%" value={form.purity} onChange={(e) => set("purity", e.target.value)} />
            </div>
          </div>
          {form.price && form.cost ? (
            <p className="-mt-2 font-mono text-[11px] text-cyan">
              Margin: {(((parseFloat(form.price) - parseFloat(form.cost)) / parseFloat(form.price)) * 100).toFixed(1)}% · Profit/unit: ${(parseFloat(form.price) - parseFloat(form.cost)).toFixed(2)}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Molecular Formula</label>
              <input className={inputCls} value={form.molecular_formula} onChange={(e) => set("molecular_formula", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>CAS Number</label>
              <input className={inputCls} value={form.cas_number} onChange={(e) => set("cas_number", e.target.value)} />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Sequence</label>
              <input className={inputCls} value={form.sequence} onChange={(e) => set("sequence", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Batch / Lot No.</label>
              <input data-testid="form-batch" className={inputCls} placeholder="QHL-2026-001" value={form.batch_number} onChange={(e) => set("batch_number", e.target.value)} />
            </div>
          </div>

          {/* Uploads */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Product Image</label>
              <label
                data-testid="upload-image-label"
                className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-cyan/30 bg-navy px-3 py-3 font-mono text-xs uppercase tracking-wider text-teal-muted transition-colors hover:border-cyan/60 hover:text-cyan"
              >
                {uploading.image ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />}
                {form.image_url ? "Replace Image" : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "image")}
                />
              </label>
              {form.image_url && (
                <img src={form.image_url} alt="preview" className="mt-2 h-20 w-20 border border-cyan/15 object-cover" />
              )}
            </div>
            <div>
              <label className={labelCls}>Certificate of Analysis (PDF)</label>
              <label
                data-testid="upload-coa-label"
                className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-cyan/30 bg-navy px-3 py-3 font-mono text-xs uppercase tracking-wider text-teal-muted transition-colors hover:border-cyan/60 hover:text-cyan"
              >
                {uploading.coa ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />}
                {form.coa_file_path ? "Replace COA" : "Upload COA"}
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "coa")}
                />
              </label>
              {form.coa_filename && (
                <p className="mt-2 truncate font-mono text-[11px] text-cyan">{form.coa_filename}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 border border-cyan/10 bg-navy px-4 py-3">
            <Switch data-testid="form-featured" checked={form.featured} onCheckedChange={(v) => set("featured", v)} />
            <span className="font-mono text-xs uppercase tracking-wider text-white">Feature on homepage</span>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="border border-cyan/20 px-5 py-2.5 font-mono text-xs uppercase tracking-wider text-teal-muted hover:text-white">
              Cancel
            </button>
            <button
              type="submit"
              data-testid="save-product-button"
              disabled={saving}
              className="flex items-center gap-2 bg-cyan px-6 py-2.5 font-mono text-xs font-semibold uppercase tracking-wider text-navy-deep hover:bg-cyan-bright disabled:opacity-60"
            >
              {saving ? <CircleNotch size={15} className="animate-spin" /> : "Save Compound"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CouponsPanel() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({
    code: "", type: "percentage", value: "", min_order: "", max_uses: "", expires_at: "", active: true,
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/coupons").then(({ data }) => setCoupons(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.value) {
      toast.error("Code and value are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/coupons", {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: parseFloat(form.value),
        active: form.active,
        min_order: form.min_order ? parseFloat(form.min_order) : null,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      });
      toast.success("Coupon created");
      setForm({ code: "", type: "percentage", value: "", min_order: "", max_uses: "", expires_at: "", active: true });
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not create coupon");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, code) => {
    if (!window.confirm(`Delete coupon "${code}"?`)) return;
    try {
      await api.delete(`/coupons/${id}`);
      toast.success("Coupon deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const inputCls = "w-full border border-cyan/15 bg-navy px-3 py-2.5 font-mono text-sm text-white placeholder:text-teal/40 outline-none focus:border-cyan/50";
  const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-teal";

  return (
    <div className="mt-8 grid gap-8 lg:grid-cols-[360px_1fr]">
      {/* Create form */}
      <form onSubmit={create} data-testid="coupon-form" className="h-fit border border-cyan/15 bg-navy-surface p-6">
        <h3 className="mb-5 font-mono text-xs uppercase tracking-widest text-cyan">// New Coupon</h3>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Code</label>
            <input data-testid="coupon-code-input" className={inputCls} value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="SUMMER25" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Type</label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="coupon-type-select" className="border-cyan/15 bg-navy font-mono text-sm text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-cyan/20 bg-navy-surface font-mono text-sm text-white">
                  <SelectItem value="percentage">Percentage %</SelectItem>
                  <SelectItem value="fixed">Fixed $</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>{form.type === "percentage" ? "Percent" : "Amount $"}</label>
              <input data-testid="coupon-value-input" type="number" step="0.01" className={inputCls} value={form.value} onChange={(e) => set("value", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Min order $</label>
              <input className={inputCls} type="number" step="0.01" value={form.min_order} onChange={(e) => set("min_order", e.target.value)} placeholder="optional" />
            </div>
            <div>
              <label className={labelCls}>Max uses</label>
              <input className={inputCls} type="number" value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} placeholder="optional" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Expires</label>
            <input className={inputCls} type="date" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} />
          </div>
          <div className="flex items-center gap-3 border border-cyan/10 bg-navy px-4 py-3">
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} />
            <span className="font-mono text-xs uppercase tracking-wider text-white">Active</span>
          </div>
          <button
            type="submit"
            data-testid="create-coupon-button"
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 bg-cyan py-3 font-mono text-xs font-semibold uppercase tracking-wider text-navy-deep hover:bg-cyan-bright disabled:opacity-60"
          >
            {saving ? <CircleNotch size={15} className="animate-spin" /> : "Create Coupon"}
          </button>
        </div>
      </form>

      {/* List */}
      <div className="overflow-x-auto border border-cyan/15 bg-navy-surface">
        <table className="w-full min-w-[560px]">
          <thead>
            <tr className="border-b border-cyan/15 text-left font-mono text-[10px] uppercase tracking-widest text-teal">
              <th className="px-5 py-4">Code</th>
              <th className="px-5 py-4">Discount</th>
              <th className="px-5 py-4">Used</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cyan/10">
            {coupons.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-10 text-center font-mono text-xs uppercase tracking-wider text-teal-muted">No coupons yet</td></tr>
            ) : coupons.map((c) => (
              <tr key={c.id} data-testid={`coupon-row-${c.code}`} className="font-mono text-sm text-white">
                <td className="px-5 py-4 text-cyan">{c.code}</td>
                <td className="px-5 py-4">{c.type === "percentage" ? `${c.value}%` : formatCurrency(c.value)}</td>
                <td className="px-5 py-4 text-teal-muted">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="px-5 py-4">
                  <span className={c.active ? "text-cyan" : "text-teal/40"}>{c.active ? "Active" : "Inactive"}</span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end">
                    <button
                      data-testid={`delete-coupon-${c.code}`}
                      onClick={() => remove(c.id, c.code)}
                      className="flex h-8 w-8 items-center justify-center border border-cyan/20 text-teal-muted hover:border-destructive/50 hover:text-destructive"
                    >
                      <Trash size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrderTracking({ order, onSaved }) {
  const [carrier, setCarrier] = useState(order.tracking_carrier || "usps");
  const [number, setNumber] = useState(order.tracking_number || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!number.trim()) {
      toast.error("Enter a tracking number");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/orders/${order.id}/tracking`, { carrier, tracking_number: number.trim() });
      toast.success("Tracking saved — customer emailed");
      onSaved();
    } catch {
      toast.error("Could not save tracking");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-cyan/10 pt-4">
      <div>
        <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-teal">Carrier</label>
        <Select value={carrier} onValueChange={setCarrier}>
          <SelectTrigger data-testid={`carrier-select-${order.id}`} className="w-[130px] border-cyan/20 bg-navy font-mono text-xs uppercase text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-cyan/20 bg-navy-surface font-mono text-xs text-white">
            <SelectItem value="usps">USPS</SelectItem>
            <SelectItem value="ups">UPS</SelectItem>
            <SelectItem value="fedex">FedEx</SelectItem>
            <SelectItem value="dhl">DHL</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 min-w-[160px]">
        <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-teal">Tracking #</label>
        <input
          data-testid={`tracking-input-${order.id}`}
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="1Z..."
          className="w-full border border-cyan/15 bg-navy px-3 py-2 font-mono text-xs text-white placeholder:text-teal/40 outline-none focus:border-cyan/50"
        />
      </div>
      <button
        data-testid={`save-tracking-${order.id}`}
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 bg-cyan px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-wider text-navy-deep hover:bg-cyan-bright disabled:opacity-60"
      >
        {saving ? <CircleNotch size={14} className="animate-spin" /> : <><FloppyDisk size={14} weight="bold" /> {order.tracking_url ? "Update" : "Ship"}</>}
      </button>
      {order.tracking_url && (
        <a
          href={order.tracking_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 border border-cyan/30 px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan hover:bg-cyan/10"
        >
          <Truck size={14} /> Track
        </a>
      )}
    </div>
  );
}

function EmailReportsCard({ sending, onSend }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border border-cyan/15 bg-navy-surface p-5">
      <div>
        <h3 className="font-mono text-xs uppercase tracking-widest text-cyan">// Email Reports</h3>
        <p className="mt-1 font-mono text-[11px] text-teal-muted">Auto: weekly (Mon 8am) · monthly (1st) · daily digest (8pm). Send one now:</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="send-weekly-report" onClick={() => onSend("weekly")} disabled={sending} className="flex items-center gap-2 border border-cyan/30 px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan transition-colors hover:bg-cyan/10 disabled:opacity-50">
          {sending === "weekly" ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />} Weekly
        </button>
        <button data-testid="send-monthly-report" onClick={() => onSend("monthly")} disabled={sending} className="flex items-center gap-2 border border-cyan/30 px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan transition-colors hover:bg-cyan/10 disabled:opacity-50">
          {sending === "monthly" ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />} Monthly
        </button>
        <button data-testid="send-digest" onClick={() => onSend("digest")} disabled={sending} className="flex items-center gap-2 border border-cyan/30 px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan transition-colors hover:bg-cyan/10 disabled:opacity-50">
          {sending === "digest" ? <CircleNotch size={14} className="animate-spin" /> : <CalendarCheck size={14} />} Daily Digest
        </button>
      </div>
    </div>
  );
}

const SHEET_DOWNLOADS = [
  { key: "xlsx", path: "/admin/sheets/template.xlsx", filename: "QuadHelix_Google_Sheet.xlsx", testid: "download-sheet-xlsx", label: "Full Workbook (.xlsx)", icon: DownloadSimple, primary: true },
  { key: "inventory", path: "/admin/sheets/inventory.csv", filename: "QuadHelix_Inventory.csv", testid: "download-sheet-inventory", label: "Inventory (.csv)", icon: FileCsv },
  { key: "profit", path: "/admin/sheets/profit.csv", filename: "QuadHelix_Profit_Report.csv", testid: "download-sheet-profit", label: "Profit Report (.csv)", icon: FileCsv },
  { key: "instructions", path: "/admin/sheets/instructions.csv", filename: "QuadHelix_How_To_Use.csv", testid: "download-sheet-instructions", label: "How to Use (.csv)", icon: FileCsv },
];

function SheetTemplateCard({ downloading, onDownload }) {
  return (
    <div className="mt-6 border border-cyan/15 bg-navy-surface p-5" data-testid="sheet-template-card">
      <div className="flex items-start gap-3">
        <Table size={22} className="mt-0.5 shrink-0 text-cyan" />
        <div>
          <h3 className="font-mono text-xs uppercase tracking-widest text-cyan">// Google Sheet Template</h3>
          <p className="mt-1 max-w-2xl font-mono text-[11px] leading-relaxed text-teal-muted">
            Download a ready-to-use workbook (live inventory, margins &amp; COA links + a pre-filled
            Profit Report and a "How to use this sheet" guide). Upload the .xlsx to Google Drive and
            choose "Open with Google Sheets" to keep all tabs.
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {SHEET_DOWNLOADS.map((d) => (
          <button
            key={d.key}
            data-testid={d.testid}
            onClick={() => onDownload(d.key, d.path, d.filename)}
            disabled={!!downloading}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan transition-colors disabled:opacity-50 ${d.primary ? "bg-cyan/15 hover:bg-cyan/25" : "border border-cyan/30 hover:bg-cyan/10"}`}
          >
            {downloading === d.key ? <CircleNotch size={14} className="animate-spin" /> : <d.icon size={14} />} {d.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SheetSyncCard({ syncStatus, syncing, onSync }) {
  return (
    <div className="mt-6 border border-cyan/15 bg-navy-surface p-5" data-testid="sheet-sync-card">
      <div className="flex items-start gap-3">
        <ArrowsClockwise size={22} className="mt-0.5 shrink-0 text-cyan" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-mono text-xs uppercase tracking-widest text-cyan">// Google Sheets Sync</h3>
            {syncStatus && (
              syncStatus.connected ? (
                <span data-testid="sync-status-connected" className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-emerald-400">
                  <CheckCircle size={14} weight="fill" /> Connected
                </span>
              ) : (
                <span data-testid="sync-status-error" className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-amber-400">
                  <WarningCircle size={14} weight="fill" /> {syncStatus.configured ? "Not connected" : "Not configured"}
                </span>
              )
            )}
          </div>
          <p className="mt-1 max-w-2xl font-mono text-[11px] leading-relaxed text-teal-muted">
            <strong className="text-white">Push</strong> writes live inventory, margins &amp; a profit report to your sheet (low-stock items are auto-highlighted).{" "}
            <strong className="text-white">Pull</strong> reads the Inventory tab, updates existing products by Product&nbsp;ID, and{" "}
            <strong className="text-white">creates a brand-new product for any row you add with a blank Product&nbsp;ID</strong> (its new ID is written back automatically).
          </p>
          <p className="mt-1 font-mono text-[11px] leading-relaxed text-teal-muted/70">
            To add a product: type a new row on the <strong className="text-white">Inventory</strong> tab (Name &amp; Price required), leave Product&nbsp;ID blank, then Pull. Auto-pulls nightly at 2:00 AM ({"America/New_York"}).
          </p>
          {syncStatus?.connected && (
            <p className="mt-2 font-mono text-[11px] text-teal-muted" data-testid="sync-sheet-title">
              Sheet: <span className="text-white">{syncStatus.title}</span>
              {syncStatus.sheet_url && (
                <a href={syncStatus.sheet_url} target="_blank" rel="noreferrer" data-testid="open-sheet-link" className="ml-2 inline-flex items-center gap-1 text-cyan hover:text-cyan-bright">
                  Open <ArrowSquareOut size={12} />
                </a>
              )}
            </p>
          )}
          {syncStatus && !syncStatus.connected && syncStatus.error && (
            <p className="mt-2 max-w-2xl font-mono text-[11px] leading-relaxed text-amber-400" data-testid="sync-error-detail">
              {syncStatus.error}
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          data-testid="sync-push-button"
          onClick={() => onSync("push")}
          disabled={!!syncing || !syncStatus?.connected}
          className="flex items-center gap-2 bg-cyan/15 px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan transition-colors hover:bg-cyan/25 disabled:opacity-50"
        >
          {syncing === "push" ? <CircleNotch size={14} className="animate-spin" /> : <ArrowLineUp size={14} />} Push to Sheet
        </button>
        <button
          data-testid="sync-pull-button"
          onClick={() => onSync("pull")}
          disabled={!!syncing || !syncStatus?.connected}
          className="flex items-center gap-2 border border-cyan/30 px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan transition-colors hover:bg-cyan/10 disabled:opacity-50"
        >
          {syncing === "pull" ? <CircleNotch size={14} className="animate-spin" /> : <ArrowLineDown size={14} />} Pull from Sheet
        </button>
      </div>
    </div>
  );
}

function ProfitOverview({ period, setPeriod, summary, loading, data }) {
  return (
    <>
      {/* Period selector */}
      <div className="mt-6 flex items-center gap-3">
        <ChartLineUp size={18} className="text-cyan" />
        <span className="font-mono text-xs uppercase tracking-widest text-teal">Profit overview</span>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger data-testid="profit-period-select" className="ml-auto w-[160px] border-cyan/20 bg-navy-surface font-mono text-xs uppercase text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-cyan/20 bg-navy-surface font-mono text-xs text-white">
            <SelectItem value="weekly">Last 7 days</SelectItem>
            <SelectItem value="monthly">Last 30 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="mt-4 grid gap-px bg-cyan/10 sm:grid-cols-2 lg:grid-cols-4">
        {summary.map((s) => (
          <div key={s.label} className="bg-navy-surface p-5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-teal">{s.label}</span>
            <p className={`mt-2 font-mono text-2xl font-bold ${s.accent ? "text-cyan" : "text-white"}`} data-testid={`profit-${s.label.replace(/\s/g,'-').toLowerCase()}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Product breakdown */}
      <div className="mt-6 overflow-x-auto border border-cyan/15 bg-navy-surface">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-cyan/15 text-left font-mono text-[10px] uppercase tracking-widest text-teal">
              <th className="px-5 py-4">Product</th>
              <th className="px-5 py-4 text-right">Units</th>
              <th className="px-5 py-4 text-right">Revenue</th>
              <th className="px-5 py-4 text-right">COGS</th>
              <th className="px-5 py-4 text-right">Profit</th>
              <th className="px-5 py-4 text-right">Margin</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cyan/10">
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center"><CircleNotch size={20} className="mx-auto animate-spin text-cyan" /></td></tr>
            ) : !data || data.rows.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center font-mono text-xs uppercase tracking-wider text-teal-muted">No paid orders in this period</td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.name} className="font-mono text-sm text-white">
                <td className="px-5 py-4">{r.name}</td>
                <td className="px-5 py-4 text-right text-teal-muted">{r.units}</td>
                <td className="px-5 py-4 text-right">{formatCurrency(r.revenue)}</td>
                <td className="px-5 py-4 text-right text-teal-muted">{formatCurrency(r.cost)}</td>
                <td className="px-5 py-4 text-right text-cyan">{formatCurrency(r.profit)}</td>
                <td className="px-5 py-4 text-right text-sky-300">{r.margin}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ReportsPanel() {
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState("");
  const [downloading, setDownloading] = useState("");
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/profit?period=${period}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const loadSyncStatus = useCallback(() => {
    api.get("/admin/sheets/sync/status").then(({ data }) => setSyncStatus(data)).catch(() => setSyncStatus(null));
  }, []);
  useEffect(() => { loadSyncStatus(); }, [loadSyncStatus]);

  const sendReport = async (type) => {
    setSending(type);
    try {
      const { data } = await api.post(`/admin/reports/${type}`);
      toast.success(data.message);
    } catch {
      toast.error("Could not send. Check email configuration.");
    } finally {
      setSending("");
    }
  };

  const downloadSheet = async (key, path, filename) => {
    setDownloading(key);
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch {
      toast.error("Could not generate the file. Please try again.");
    } finally {
      setDownloading("");
    }
  };

  const runSync = async (direction) => {
    setSyncing(direction);
    try {
      const { data } = await api.post(`/admin/sheets/sync/${direction}`);
      toast.success(data.message);
      loadSyncStatus();
      if (direction === "pull") load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed. Please try again.");
    } finally {
      setSyncing("");
    }
  };

  const summary = [
    { label: "Net Revenue", value: data ? formatCurrency(data.net_revenue) : "—" },
    { label: "Cost of Goods", value: data ? formatCurrency(data.total_cost) : "—" },
    { label: "Gross Profit", value: data ? formatCurrency(data.gross_profit) : "—", accent: true },
    { label: "Margin", value: data ? `${data.margin}%` : "—", accent: true },
  ];

  return (
    <div className="mt-8">
      <EmailReportsCard sending={sending} onSend={sendReport} />
      <SheetTemplateCard downloading={downloading} onDownload={downloadSheet} />
      <SheetSyncCard syncStatus={syncStatus} syncing={syncing} onSync={runSync} />
      <ProfitOverview period={period} setPeriod={setPeriod} summary={summary} loading={loading} data={data} />
    </div>
  );
}

function MessagesPanel({ onChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/contact").then(({ data }) => setMessages(data)).catch(() => setMessages([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      const fd = new FormData();
      fd.append("status", status);
      await api.put(`/contact/${id}/status`, fd);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
      onChange && onChange();
    } catch {
      toast.error("Could not update message");
    }
  };

  return (
    <div className="mt-8 space-y-4">
      {loading && (
        <div className="border border-cyan/10 bg-navy-surface p-10 text-center">
          <CircleNotch size={20} className="mx-auto animate-spin text-cyan" />
        </div>
      )}
      {!loading && messages.length === 0 && (
        <div className="border border-cyan/15 bg-navy-surface p-14 text-center font-mono text-sm uppercase tracking-wider text-teal-muted" data-testid="no-messages">
          No messages yet
        </div>
      )}
      {!loading && messages.length > 0 && messages.map((m) => (
        <div key={m.id} data-testid={`message-${m.id}`} className={`border bg-navy-surface p-5 ${m.status === "new" ? "border-cyan/30" : "border-cyan/10"}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-mono text-sm text-white">{m.subject || "Customer enquiry"}</p>
              <p className="mt-0.5 font-mono text-[11px] text-teal-muted">{m.name} · {m.email}</p>
              <p className="font-mono text-[11px] text-teal">{new Date(m.created_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${m.status === "new" ? "border-cyan/40 text-cyan" : "border-emerald-400/40 text-emerald-300"}`}>
                {m.status}
              </span>
              <button
                data-testid={`resolve-message-${m.id}`}
                onClick={() => setStatus(m.id, m.status === "resolved" ? "new" : "resolved")}
                className="border border-cyan/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-teal-muted transition-colors hover:border-cyan/50 hover:text-cyan"
              >
                {m.status === "resolved" ? "Reopen" : "Mark Resolved"}
              </button>
            </div>
          </div>
          <p className="mt-3 whitespace-pre-wrap border-l-2 border-cyan/30 bg-navy px-4 py-3 font-mono text-sm leading-relaxed text-teal-muted">{m.message}</p>
          <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "Your QuadHelix enquiry")}`} className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-cyan hover:text-cyan-bright">
            <EnvelopeSimple size={14} /> Reply by email
          </a>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      loadAll();
    } catch {
      toast.error("Could not delete");
    }
  };

  const changeStatus = async (orderId, status) => {
    try {
      const fd = new FormData();
      fd.append("status", status);
      await api.put(`/orders/${orderId}/status`, fd);
      toast.success("Order updated");
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
    } catch {
      toast.error("Could not update order");
    }
  };

  const markPaid = async (orderId) => {
    try {
      const { data } = await api.post(`/orders/${orderId}/mark-paid`);
      toast.success(data.message);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark order paid");
    }
  };

  return (
    <div data-testid="admin-page" className="mx-auto max-w-7xl px-6 py-12 sm:px-8 lg:px-12">
      <div className="border-b border-cyan/15 pb-6">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-cyan">// Control Room</span>
        <h1 className="mt-2 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">
          Admin Dashboard
        </h1>
      </div>

      {/* Stats */}
      <div className="mt-8 grid gap-px bg-cyan/10 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={CurrencyDollar} label="Revenue (Paid)" value={stats ? formatCurrency(stats.revenue) : "—"} />
        <StatCard icon={Receipt} label="Total Orders" value={stats?.total_orders ?? "—"} />
        <StatCard icon={Package} label="Products" value={stats?.total_products ?? "—"} />
        <StatCard icon={Users} label="Customers" value={stats?.total_customers ?? "—"} />
      </div>

      {/* Tabs */}
      <div className="mt-10 flex gap-1 border-b border-cyan/15">
        {[
          { id: "products", label: "Products" },
          { id: "orders", label: "Orders" },
          { id: "coupons", label: "Coupons" },
          { id: "reports", label: "Reports" },
          { id: "messages", label: "Messages" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-5 py-3 font-mono text-xs uppercase tracking-widest transition-colors ${
              tab === t.id ? "border-cyan text-cyan" : "border-transparent text-teal-muted hover:text-white"
            }`}
          >
            {t.label}
            {t.id === "messages" && stats?.new_messages > 0 && (
              <span data-testid="new-messages-badge" className="ml-2 inline-flex h-4 min-w-4 items-center justify-center bg-cyan px-1 font-mono text-[9px] font-bold text-navy-deep">
                {stats.new_messages}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Products tab */}
      {tab === "products" && (
        <div className="mt-8">
          <div className="mb-6 flex justify-end">
            <button
              data-testid="add-product-button"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
              className="flex items-center gap-2 bg-cyan px-5 py-2.5 font-mono text-xs font-semibold uppercase tracking-wider text-navy-deep transition-all hover:bg-cyan-bright hover:shadow-glow"
            >
              <Plus size={16} weight="bold" /> Add Compound
            </button>
          </div>

          <div className="overflow-x-auto border border-cyan/15 bg-navy-surface">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-cyan/15 text-left font-mono text-[10px] uppercase tracking-widest text-teal">
                  <th className="px-5 py-4">Compound</th>
                  <th className="px-5 py-4">Category</th>
                  <th className="px-5 py-4">Price</th>
                  <th className="px-5 py-4">Stock</th>
                  <th className="px-5 py-4">Featured</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cyan/10">
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-${p.id}`} className="font-mono text-sm text-white">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <img src={p.image_url} alt="" className="h-10 w-10 border border-cyan/15 object-cover" />
                        {p.name}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-teal-muted">{p.category}</td>
                    <td className="px-5 py-4">{formatCurrency(p.price)}</td>
                    <td className="px-5 py-4 text-teal-muted">{p.stock}</td>
                    <td className="px-5 py-4">
                      {p.featured ? <span className="text-cyan">●</span> : <span className="text-teal/30">○</span>}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          data-testid={`edit-product-${p.id}`}
                          onClick={() => {
                            setEditing(p);
                            setDialogOpen(true);
                          }}
                          className="flex h-8 w-8 items-center justify-center border border-cyan/20 text-teal-muted hover:border-cyan/50 hover:text-cyan"
                        >
                          <PencilSimple size={15} />
                        </button>
                        <button
                          data-testid={`delete-product-${p.id}`}
                          onClick={() => deleteProduct(p.id, p.name)}
                          className="flex h-8 w-8 items-center justify-center border border-cyan/20 text-teal-muted hover:border-destructive/50 hover:text-destructive"
                        >
                          <Trash size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Orders tab */}
      {tab === "orders" && (
        <div className="mt-8">
          {orders.length === 0 ? (
            <div className="border border-cyan/15 bg-navy-surface p-14 text-center font-mono text-sm uppercase tracking-wider text-teal-muted" data-testid="admin-no-orders">
              No orders yet
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-cyan/15 bg-navy-surface p-5">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="font-mono text-sm text-white">#{o.id.slice(0, 8).toUpperCase()}</p>
                      <p className="font-mono text-[11px] text-teal-muted">{o.user_email}</p>
                      <p className="font-mono text-[11px] text-teal">
                        {new Date(o.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-lg font-bold text-cyan">{formatCurrency(o.total)}</p>
                      <p className="font-mono text-[11px] text-teal-muted">{o.items.length} items</p>
                    </div>
                    <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                      <SelectTrigger
                        data-testid={`order-status-${o.id}`}
                        className="w-[170px] border-cyan/20 bg-navy font-mono text-xs uppercase tracking-wider text-white"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-cyan/20 bg-navy-surface font-mono text-xs text-white">
                        {ORDER_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 border-t border-cyan/10 pt-3 font-mono text-[11px] text-teal-muted">
                    {o.items.map((it, idx) => (
                      <span key={`${it.product_id}-${idx}`}>{it.quantity}× {it.name}</span>
                    ))}
                  </div>
                  {o.shipping?.name && (
                    <p className="mt-2 font-mono text-[11px] text-teal/70">
                      Ship to: {o.shipping.name}, {o.shipping.address}, {o.shipping.city} {o.shipping.zip}, {o.shipping.country}
                    </p>
                  )}
                  {o.status === "awaiting_payment" && (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-amber-400/30 bg-amber-400/[0.06] px-4 py-3">
                      <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-amber-300">
                        <Receipt size={15} weight="fill" />
                        {o.payment_method === "invoice" ? "Invoice — awaiting payment" : "Awaiting payment"}
                      </span>
                      <button
                        data-testid={`mark-paid-${o.id}`}
                        onClick={() => markPaid(o.id)}
                        className="flex items-center gap-2 bg-cyan px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-wider text-navy-deep transition-colors hover:bg-cyan-bright"
                      >
                        <CheckCircle size={14} weight="fill" /> Mark as Paid
                      </button>
                    </div>
                  )}
                  <OrderTracking order={o} onSaved={loadAll} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Coupons tab */}
      {tab === "coupons" && <CouponsPanel />}

      {/* Reports tab */}
      {tab === "reports" && <ReportsPanel />}

      {/* Messages tab */}
      {tab === "messages" && <MessagesPanel onChange={loadAll} />}

      <ProductFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        product={editing}
        onSaved={loadAll}
      />
    </div>
  );
}
